import { z } from "zod";
// Vocabulario do ciclo remessa/retorno. Ver specs/domain/glossario.md e
// specs/adrs/0001-ciclo-remessa-retorno.md.
/** O que a folha de pagamento de fato fez com um desconto instruido numa
 *  competencia. `recebida` e' o estado ANTES do retorno — nao existe como
 *  codigo no arquivo, so os quatro seguintes. */
export const RESULTADO_DESCONTO = [
    "recebida",
    "aplicada",
    "parcial",
    "falha",
    "interrompida_desligamento",
    /**
     * O SERVIDOR nao teve folha na competencia — licenca, cessao, afastamento.
     * Nao e' falha: a prefeitura nao deixou de descontar, nao havia salario do
     * qual descontar.
     *
     * Observado no dado real do I.M.A.M.N. (26/08/2026): dois servidores
     * `EFETIVO`, ativos no cadastro, com o holerite inteiro zerado — e as
     * consignacoes deles sumiram do arquivo na parcela 20 de 120. O cadastro
     * dizia ativo e a folha dizia zero. Classificar como `falha` acusaria a
     * prefeitura e dispararia cascata de inadimplencia sobre quem esta de
     * licenca; classificar como quitacao liberaria margem de divida viva.
     */
    "suspensa_sem_folha",
    /**
     * A ATLAS suspendeu o desconto na remessa para caber no TETO GLOBAL da regra
     * do orgao (#277, `quem_corta = atlas`). Nao e' falha: a folha fez o que o
     * movimento mandou. Nao avanca, nao amortiza, nao deixa residuo — como
     * `suspensa_sem_folha` —, e a divida continua viva e consumindo margem.
     * Nao vem no arquivo da folha: quem o produz e' o retorno, lendo o corte.
     */
    "suspensa_por_teto",
];
/** Codigos que trafegam no registro de resultado do retorno. */
export const CODIGO_RESULTADO = {
    AP: "aplicada",
    PA: "parcial",
    FA: "falha",
    ID: "interrompida_desligamento",
    SF: "suspensa_sem_folha",
};
export const CODIGOS_RESULTADO = Object.keys(CODIGO_RESULTADO);
/**
 * O STATUS NA FOLHA de um contrato numa competencia, como o bate-carteira o
 * mostra (#347). E' o `ResultadoDesconto` dobrado em cinco respostas para quem
 * confere a carteira: descontou, descontou em parte, nao descontou, ficou
 * suspenso, ou o retorno ainda nao chegou. O mapeamento mora num lugar so' —
 * `statusNaFolha` em `@atlas/domain` —, usado pela API, pelo arquivo e pelas telas.
 */
export const STATUS_NA_FOLHA = ["aplicada", "parcial", "nao_descontada", "suspensa", "aguardando_retorno"];
/** De onde veio o resultado. `presumido` e' o fechamento pela data de repasse
 *  sem retorno; `manual` e' a averbadora decidindo na tela. */
export const ORIGEM_RESULTADO = ["retorno", "manual", "presumido"];
/** Mudanca de pessoal DECLARADA pela prefeitura dentro do retorno. Nao e' mais
 *  um arquivo proprio (o antigo "Movimentacao"). Desligamento e mudanca de
 *  vinculo sao declarados porque disparam efeito irreversivel em terceiro;
 *  salario e cargo saem do diff contra o snapshot anterior. */
export const MOVIMENTACAO_TIPO = ["admissao", "demissao", "aposentadoria", "alteracao_vinculo"];
export const CODIGO_MOVIMENTACAO = {
    AD: "admissao",
    DE: "demissao",
    AO: "aposentadoria",
    AV: "alteracao_vinculo",
};
export const CODIGOS_MOVIMENTACAO = Object.keys(CODIGO_MOVIMENTACAO);
/** Situacao do servidor no snapshot de margem. `SU` = sem folha nesta
 *  competencia (licenca, cessao, afastamento) — nao e' desligamento, que precisa
 *  vir declarado. */
export const SITUACAO_SNAPSHOT = ["AT", "SU"];
/** Estado de conferencia de uma competencia fechada. `presumida` = fechou na
 *  data de repasse sem o retorno ter chegado. */
export const CONFERENCIA_COMPETENCIA = ["confirmada", "presumida"];
/**
 * Competencia `AAAAMM` com MES REAL, 01 a 12.
 *
 * O regex antigo era `^\d{6}$`, que aceita `202613`. Nao e' um detalhe de
 * validacao: com mes 13 o corte da folha sai `2026-13-15T17:00:00-03:00`, que
 * `Date.parse` recusa, e `corteJaPassou` le isso como "nao passou" — a folha fica
 * aberta PARA SEMPRE, sem erro em lugar nenhum. Existe uma folha `202613` no banco
 * de dev, criada por um escopo de teste errado; foi assim que o defeito apareceu.
 *
 * Mora em `@atlas/types` porque e' o unico pacote que api, domain, sdk e web todos
 * podem importar — a checagem tinha 14 copias espalhadas, e copia divergente foi
 * exatamente o que produziu a folha quebrada.
 */
export const RE_COMPETENCIA = /^\d{4}(?:0[1-9]|1[0-2])$/;
export function competenciaValida(v) {
    return RE_COMPETENCIA.test((v ?? "").trim());
}
export const CompetenciaSchema = z
    .string()
    .regex(RE_COMPETENCIA, "competencia deve ser AAAAMM com mes entre 01 e 12");
export const ResultadoDescontoSchema = z.object({
    adf: z.string(),
    competencia: CompetenciaSchema,
    resultado: z.enum(RESULTADO_DESCONTO),
    valor_instruido: z.number(),
    valor_efetivo: z.number(),
    origem: z.enum(ORIGEM_RESULTADO),
    motivo: z.string().nullable(),
    registrado_em: z.string(),
});
export const DescontoNaoSolicitadoSchema = z.object({
    id: z.string(),
    prefeitura_id: z.number().int(),
    competencia: CompetenciaSchema,
    matricula: z.string(),
    codigo_verba: z.string(),
    valor: z.number(),
    consignataria: z.string().nullable(),
    situacao: z.enum(["quarentena", "aceito", "recusado"]),
    visto_em: z.string(),
});
/** Resumo devolvido ao subir um retorno. `rejeitado` = um registro de regime
 *  tudo-ou-nada falhou, ou header/trailer nao fecharam: nada foi aplicado. */
export const RetornoResumoSchema = z.object({
    rejeitado: z.boolean(),
    motivo_rejeicao: z.string().nullable(),
    competencia: CompetenciaSchema.nullable(),
    resultados_aplicados: z.number().int(),
    nao_solicitados_em_quarentena: z.number().int(),
    movimentacoes_declaradas: z.number().int(),
    servidores_no_snapshot: z.number().int(),
    alteracoes_por_diff: z.number().int(),
    linhas_rejeitadas: z.array(z.object({
        linha: z.number().int(),
        tipo_registro: z.string(),
        erros: z.array(z.object({ campo: z.string(), mensagem: z.string() })),
    })),
});
/** Resposta do envio de retorno em MODO MANUAL (HTTP 202): o arquivo foi
 *  recebido e guardado, mas os descontos ainda NAO foram aplicados — a Atlas
 *  processa e confirma depois. Discriminante `recebido: true` (a variante
 *  automatica usa `rejeitado`). */
export const RetornoRecebidoSchema = z.object({
    recebido: z.literal(true),
    arquivo_id: z.string(),
    competencia: z.string(),
    parte: z.string().nullable(),
    aguardando_processamento: z.literal(true),
    mensagem: z.string(),
    /** O layout em que o pacote chegou, e se algo precisou ser traduzido.
     *  Opcional: respostas anteriores a 02/09/2026 nao o traziam. */
    layout: z.object({ codigo: z.string(), versao: z.string(), semTraducao: z.boolean() }).optional(),
    /** Resumo do relatorio de conversao retido, com a chave do completo. */
    relatorioConversao: z.record(z.string(), z.union([z.number(), z.string(), z.null()])).optional(),
});
//# sourceMappingURL=folha.js.map